"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateConsentDocumentForPublish = exports.collectConsentPublishValidationErrors = exports.isPtsOnlyConsentDocument = exports.CONSENT_PUBLISH_VALIDATION_MESSAGES = exports.SOURCE_FIELD_MAX_LENGTH = void 0;
const choice_options_1 = require("./choice_options");
const document_screens_1 = require("./document_screens");
const element_custom_data_1 = require("./element_custom_data");
const signer_roles_1 = require("./signer_roles");
const template_custom_data_1 = require("./template_custom_data");
const types_1 = require("./types");
const RUNTIME_FIELD_ELEMENT_TYPES = new Set([
    'SIGNATURE',
    'INITIALS',
    'TEXT_INPUT',
    'DATE_INPUT',
    'CHOICE',
    'SINGLE_SELECT',
    'MULTI_SELECT',
]);
const MIN_CHOICE_OPTIONS = 2;
/** Cortex's limit on the runtime's source version and label, which MOM writes trimmed. */
exports.SOURCE_FIELD_MAX_LENGTH = 512;
const hasDuplicate = (values) => new Set(values).size !== values.length;
const isRuntimeFieldElement = (type) => RUNTIME_FIELD_ELEMENT_TYPES.has(type);
exports.CONSENT_PUBLISH_VALIDATION_MESSAGES = {
    languageMissing: 'Select a document language before publishing.',
    noRoles: 'Add at least one signer role before publishing.',
    noSigningRoles: 'At least one signer role must sign; a non-signing participant cannot be the only role.',
    signerTypeMissing: 'Each signer role must have a signer type before publishing.',
    signerTypeUnknown: 'Signer role "{role}" has an unrecognized signer type. Choose its signer type in the Roles tab before publishing.',
    roleKeyMissing: 'Each signer role must have a stable key before publishing.',
    duplicateRoleKeys: 'Signer role keys must be unique before publishing.',
    duplicateRoleLabels: 'Signer role labels must be unique before publishing.',
    fieldKeyMissing: 'Every signature, initials, and data field must have a stable key before publishing.',
    signerRoleMissing: 'Assign a signer role to every signature, initials, and data field before publishing.',
    signerRoleInvalid: 'Each signature, initials, and data field must reference a role defined in the Roles tab.',
    signerRoleNonSigning: 'A non-signing participant cannot be assigned a signature, initials, or data field.',
    runtimeFieldsMissing: 'Configure at least one signature, initials, or data field before publishing.',
    duplicateFieldKeys: 'Field keys must be unique before publishing.',
    choiceTooFewOptions: 'Each single/multi-select question must have at least two options before publishing.',
    choiceOptionsUnplaced: '"{element}" has {count, plural, one {# response option that is} other {# response options that are}} not placed on the document yet.',
    choiceOptionsUnplacedUnnamed: 'A response question on page {page} has {count, plural, one {# response option that is} other {# response options that are}} not placed on the document yet.',
    choiceOptionKeysInvalid: 'Single/multi-select option keys must be unique sequential integers starting at 1 before publishing.',
    choiceOptionValueInvalid: 'Single/multi-select reference labels cannot contain "=" before publishing.',
    elementCustomDataIncomplete: 'Incomplete configuration data on {list}. Give every key a value or remove the row.',
    screenOverLimit: 'This screen is {current}/{max} characters and cannot be saved.',
    elementUnplaced: 'Place every copied element on the document before publishing.',
    carryForwardUnconfirmed: 'Confirm copied element positions before publishing.',
    dimensionMismatch: 'Confirm element positions that landed on a page with different dimensions.',
    textInputTypeMissing: '"{element}" is missing an input type.',
    textInputTypeMissingUnnamed: 'A text field on page {page} is missing an input type.',
    textInputSignerRoleMissing: '"{element}" is missing a signer role.',
    textInputSignerRoleMissingUnnamed: 'A text field on page {page} is missing a signer role.',
    textInputPlacementMissing: '"{element}" is not placed on the document yet.',
    textInputPlacementMissingUnnamed: 'A text field is not placed on the document yet.',
    pdfMissing: 'Attach a PDF before publishing.',
    sourceLabelOverLimit: 'The source label is {current}/{max} characters and cannot be published.',
    sourceVersionOverLimit: 'The source version is {current}/{max} characters and cannot be published.',
};
const failure = (message, params, locate) => (Object.assign({ message,
    params }, locate));
const overlayItemIdForElement = (element, optionKey) => {
    var _a;
    if (optionKey !== undefined) {
        return `consent-choice-option-${element.id}-${optionKey}`;
    }
    if ((0, choice_options_1.isChoiceElementType)(element.type)) {
        const first = (_a = element.config.ec__options) === null || _a === void 0 ? void 0 : _a[0];
        return first ? `consent-choice-option-${element.id}-${first.key}` : undefined;
    }
    return `consent-element-marker-${element.id}`;
};
const locate = (element, optionKey) => ({
    elementId: element.id,
    overlayItemId: overlayItemIdForElement(element, optionKey),
});
const textInputFailure = (element, named, unnamed) => {
    var _a;
    const name = (_a = element.config.ec__label) === null || _a === void 0 ? void 0 : _a.trim();
    return name
        ? failure(named, { element: name }, locate(element))
        : failure(unnamed, { page: element.page }, locate(element));
};
/**
 * The checks only a free-text field has. A missing signer role is named the same way as a missing
 * input type. An unknown role and a non-signing role stay on the shared sentences below.
 */
const validateTextInputForPublish = (element) => {
    // A type outside the list is as unanswerable as none: the compiler refuses both.
    const inputType = element.config.ec__input_type;
    if (!inputType || !types_1.CONSENT_TEXT_INPUT_TYPES.includes(inputType)) {
        return textInputFailure(element, exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.textInputTypeMissing, exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.textInputTypeMissingUnnamed);
    }
    if (!(element.page >= 1)) {
        return textInputFailure(element, exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.textInputPlacementMissing, exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.textInputPlacementMissingUnnamed);
    }
    return null;
};
/** True when the option keys are exactly 1..n with no gaps or duplicates. */
const hasSequentialKeys = (keys) => {
    const sorted = [...keys].sort((a, b) => a - b);
    return sorted.every((key, index) => key === index + 1);
};
/**
 * A template with at least one signer role but no digital fields is a valid authoring state for a
 * Print-to-Sign-only template — the role (typically Participant) is what an invite gets issued
 * against, so it's still required even for PTS; only the digital signature/data fields are
 * eSign-specific and can be skipped (the PTS/eSign choice lives on the signed document at creation
 * time, not the template; see use_document_templates.hook.ts). A template with zero roles is never
 * valid — there'd be nothing to issue an invite against — and every other combination (fields with
 * no valid role reference, etc.) still indicates an incomplete eSign setup and stays blocked below.
 */
const isPtsOnlyConsentDocument = (document) => document.roles.length > 0 && document.elements.length === 0;
exports.isPtsOnlyConsentDocument = isPtsOnlyConsentDocument;
const choiceFailures = (element) => {
    var _a;
    const errors = [];
    const options = (_a = element.config.ec__options) !== null && _a !== void 0 ? _a : [];
    if (options.length < MIN_CHOICE_OPTIONS) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.choiceTooFewOptions, undefined, locate(element)));
    }
    // Being on a page other than the question's own is a normal placement, not a missing one — an
    // option carries its own page precisely so it can sit on a different one from its siblings
    // (SSP-4169 clause 9). Only `placed` says whether the author has positioned it.
    const unplaced = options.filter((option) => !option.placed);
    if (unplaced.length > 0) {
        // Named by the recorded question: it is what the author typed and what they see on the
        // element, so it points at the right question on a document with several. An element with no
        // question yet is placed by page instead — the ids that would otherwise stand in for a name
        // are machine-generated and would send the author looking for a string that is nowhere on screen.
        const questionLabel = (0, choice_options_1.choiceQuestionLabel)(element.config);
        const firstUnplaced = [...unplaced].sort((a, b) => a.page - b.page || a.position.yPct - b.position.yPct)[0];
        const target = locate(element, firstUnplaced.key);
        errors.push(questionLabel
            ? failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.choiceOptionsUnplaced, { element: questionLabel, count: unplaced.length }, target)
            : failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.choiceOptionsUnplacedUnnamed, 
            // The unplaced options' own earliest page, not `element.page`: the question's page is
            // only where it was first dropped, so once options span pages that number can name a
            // page holding none of them and send the author looking at the wrong one. An unplaced
            // option still renders at its default position on its own page, so this points at a
            // page where at least one of them is actually on screen.
            { page: Math.min(...unplaced.map((option) => option.page)), count: unplaced.length }, target));
    }
    const optionKeys = options.map((option) => option.key);
    if (hasDuplicate(optionKeys.map(String)) || !hasSequentialKeys(optionKeys)) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.choiceOptionKeysInvalid, undefined, locate(element)));
    }
    // Options are serialized to Cortex as `key=value` pairs joined by `=,`. Cortex's own
    // ec__document_processor splits each pair with `v.split('=')` and keeps only the second part, so
    // a reference label containing `=` arrives truncated there while the JS readers keep it whole —
    // the same value would match on one surface and not the other. Commas are safe: `=,` is what
    // makes them safe, which is why only `=` is rejected here.
    if (options.some((option) => option.value.includes('='))) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.choiceOptionValueInvalid, undefined, locate(element)));
    }
    return errors;
};
/**
 * Every publish blocker, in the order Studio checks them, so a caller can fix all of them in one
 * pass. The first entry is exactly what {@link validateConsentDocumentForPublish} reports.
 */
const collectConsentPublishValidationErrors = (document) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    const errors = [];
    if (!((_a = document.language) === null || _a === void 0 ? void 0 : _a.trim())) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.languageMissing));
    }
    if (!((_b = document.assetId) === null || _b === void 0 ? void 0 : _b.trim())) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.pdfMissing));
    }
    const ptsOnly = (0, exports.isPtsOnlyConsentDocument)(document);
    // Stored data can hold a role with no signer type or key (an agent can write that shape). Read
    // them as blank so signerTypeMissing / roleKeyMissing report it alongside every other rule.
    const normalizedRoles = document.roles.map((role) => {
        var _a, _b;
        return (Object.assign(Object.assign({}, role), { id: (_a = role.id) !== null && _a !== void 0 ? _a : '', role: (0, signer_roles_1.consentRoleLabel)(role), signerTypeId: ((_b = role.signerTypeId) !== null && _b !== void 0 ? _b : '').trim() }));
    });
    if (document.roles.length === 0) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.noRoles));
    }
    // A non-signing participant's invite is created already complete, so a template whose every role is
    // non-signing would reach complete without anybody having signed it. FRS 7 allows the participant
    // role to be non-signing only while some other role signs (SSP-4198).
    // Guarded on a non-empty list: `every` over no roles is true, and `noRoles` already says it.
    if (document.roles.length > 0 && normalizedRoles.every((role) => (0, signer_roles_1.isNonSigningSignerType)(role.signerTypeId))) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.noSigningRoles));
    }
    if (normalizedRoles.some((role) => role.signerTypeId.length === 0)) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.signerTypeMissing));
    }
    // A legacy MOM type such as `site` is never read as `participant` (see consentRolesFromData), so it
    // has to stop here: compiled as is, nobody in Studio's signer types would be asked to sign.
    for (const role of normalizedRoles) {
        if (role.signerTypeId.length > 0 && !(0, signer_roles_1.isKnownSignerType)(role.signerTypeId)) {
            errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.signerTypeUnknown, { role: role.role }));
        }
    }
    const roleKeys = normalizedRoles.map((role) => role.id.trim());
    if (roleKeys.some((roleKey) => roleKey.length === 0)) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.roleKeyMissing));
    }
    if (hasDuplicate(roleKeys)) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.duplicateRoleKeys));
    }
    const roleNames = normalizedRoles.map((role) => role.role);
    if (hasDuplicate(roleNames)) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.duplicateRoleLabels));
    }
    const rolesByName = new Map(normalizedRoles.map((role) => [role.role, role]));
    const runtimeFieldKeys = [];
    let runtimeFieldCount = 0;
    const { carryForward } = document;
    const totalPages = (_c = document.totalPages) !== null && _c !== void 0 ? _c : 0;
    if (carryForward && !carryForward.failed && carryForward.placementsConfirmed === false) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.carryForwardUnconfirmed));
    }
    for (const element of document.elements) {
        // No position is an agent payload Studio never writes; the compiler sorts and places fields on it.
        if (element.placed === false ||
            !element.position ||
            (carryForward && totalPages > 0 && (element.page < 1 || element.page > totalPages))) {
            errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.elementUnplaced, undefined, locate(element)));
        }
        if (element.needsDimensionConfirmation) {
            errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.dimensionMismatch, undefined, locate(element)));
        }
        if (!isRuntimeFieldElement(element.type)) {
            continue;
        }
        runtimeFieldCount += 1;
        const elementKey = (_e = (_d = element.config.ec__key) === null || _d === void 0 ? void 0 : _d.trim()) !== null && _e !== void 0 ? _e : '';
        if (elementKey) {
            runtimeFieldKeys.push(elementKey);
        }
        else {
            errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.fieldKeyMissing, undefined, locate(element)));
        }
        if (element.type === 'TEXT_INPUT') {
            const textError = validateTextInputForPublish(element);
            if (textError) {
                errors.push(textError);
            }
        }
        const signerRole = (_f = element.config.ec__signer_role) === null || _f === void 0 ? void 0 : _f.trim();
        const namedRole = signerRole ? rolesByName.get(signerRole) : undefined;
        if (!signerRole) {
            errors.push(element.type === 'TEXT_INPUT'
                ? textInputFailure(element, exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.textInputSignerRoleMissing, exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.textInputSignerRoleMissingUnnamed)
                : failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.signerRoleMissing, undefined, locate(element)));
        }
        else if (!namedRole) {
            // With no roles at all, `noRoles` already says it; flagging every field as well would bury it.
            if (document.roles.length > 0) {
                errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.signerRoleInvalid, undefined, locate(element)));
            }
        }
        else if ((0, signer_roles_1.isNonSigningSignerType)(namedRole.signerTypeId)) {
            // The Roles tab refuses the change that would create this, but a template authored before that
            // refusal existed can still carry a field on a role that never signs — it would be published as
            // a field nobody can ever complete (SSP-4198 FRS 3).
            errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.signerRoleNonSigning, undefined, locate(element)));
        }
        if ((0, choice_options_1.isChoiceElementType)(element.type)) {
            errors.push(...choiceFailures(element));
        }
    }
    // Counts fields, not keys: a field that is present but keyless is already reported above, and
    // saying "configure at least one field" beside it would be wrong.
    if (!ptsOnly && runtimeFieldCount === 0) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.runtimeFieldsMissing));
    }
    if (hasDuplicate(runtimeFieldKeys)) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.duplicateFieldKeys));
    }
    // Stored half-pairs still reach publish on templates that never went through the save-time
    // refusal (older saves, duplicates, imports). Blank rows are dropped on persist, not reported.
    const incompleteCustomData = document.elements.filter((element) => { var _a; return isRuntimeFieldElement(element.type) && ((_a = element.config.ec__custom_data) !== null && _a !== void 0 ? _a : []).some(element_custom_data_1.isHalfPairCustomDataRow); });
    if (incompleteCustomData.length > 0) {
        const list = incompleteCustomData
            .map((element) => {
            var _a;
            // Label / question text only — `ec__title` is a generated id and is nowhere on the page.
            const label = ((_a = element.config.ec__label) === null || _a === void 0 ? void 0 : _a.trim()) || (0, choice_options_1.choiceQuestionLabel)(element.config);
            return label ? `"${label}" (page ${element.page})` : `page ${element.page}`;
        })
            .join(', ');
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.elementCustomDataIncomplete, { list }, locate(incompleteCustomData[0])));
    }
    // Incomplete Custom Fields would otherwise reach Cortex as a typed unique array.
    for (const customFieldError of (0, template_custom_data_1.collectTemplateCustomFieldValidationErrors)(document.customFields)) {
        errors.push(failure(customFieldError.message, customFieldError.params));
    }
    for (const screen of [(_g = document.instructionScreen) !== null && _g !== void 0 ? _g : '', (_h = document.completionScreen) !== null && _h !== void 0 ? _h : '']) {
        if (!(0, document_screens_1.isDocumentScreenWithinLimit)(screen)) {
            errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.screenOverLimit, {
                current: screen.length,
                max: document_screens_1.DOCUMENT_SCREEN_MAX_LENGTH,
            }));
        }
    }
    const sourceLabelLength = (_k = (_j = document.sourceLabel) === null || _j === void 0 ? void 0 : _j.trim().length) !== null && _k !== void 0 ? _k : 0;
    if (sourceLabelLength > exports.SOURCE_FIELD_MAX_LENGTH) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.sourceLabelOverLimit, {
            current: sourceLabelLength,
            max: exports.SOURCE_FIELD_MAX_LENGTH,
        }));
    }
    const sourceVersionLength = (_m = (_l = document.sourceVersion) === null || _l === void 0 ? void 0 : _l.trim().length) !== null && _m !== void 0 ? _m : 0;
    if (sourceVersionLength > exports.SOURCE_FIELD_MAX_LENGTH) {
        errors.push(failure(exports.CONSENT_PUBLISH_VALIDATION_MESSAGES.sourceVersionOverLimit, {
            current: sourceVersionLength,
            max: exports.SOURCE_FIELD_MAX_LENGTH,
        }));
    }
    return errors;
};
exports.collectConsentPublishValidationErrors = collectConsentPublishValidationErrors;
const validateConsentDocumentForPublish = (document) => { var _a; return (_a = (0, exports.collectConsentPublishValidationErrors)(document)[0]) !== null && _a !== void 0 ? _a : null; };
exports.validateConsentDocumentForPublish = validateConsentDocumentForPublish;
//# sourceMappingURL=publish_validation.js.map